import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Archive, Briefcase, Loader2, Pencil, Plus } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { EmptyState } from "@/components/common/EmptyState";
import { api, getErrorMessage } from "@/lib/api";
import { JOB_STATUSES, formatSalary } from "@/lib/constants";

const JOB_STATUS_STYLES = {
  Draft: "bg-muted text-muted-foreground",
  Active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Paused: "bg-amber-50 text-amber-700 border-amber-200",
  Closed: "bg-red-50 text-red-700 border-red-200",
  Archived: "bg-slate-100 text-slate-500 border-slate-200",
};

const emptyForm = {
  title: "", hiring_brand: "", location: "", salary_min: "", salary_max: "",
  experience: "", education: "", shift: "", working_days: "", employment_type: "", work_mode: "",
  about: "", responsibilities: "", requirements: "", benefits: "", status: "Draft",
};

export default function AdminJobs() {
  const [jobs, setJobs] = useState([]);
  const [md, setMd] = useState({});
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    Promise.all([api.get("/jobs", { params: { status: "all" } }), api.get("/master-data")])
      .then(([j, m]) => {
        setJobs(j.data);
        setMd(m.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (job) => {
    setEditing(job);
    setForm({
      ...emptyForm,
      ...job,
      salary_min: job.salary_min ?? "",
      salary_max: job.salary_max ?? "",
      responsibilities: (job.responsibilities || []).join("\n"),
      requirements: (job.requirements || []).join("\n"),
      benefits: (job.benefits || []).join("\n"),
    });
    setDialogOpen(true);
  };

  const buildPayload = (overrides = {}) => ({
    title: form.title, hiring_brand: form.hiring_brand, location: form.location,
    salary_min: form.salary_min === "" ? null : Number(form.salary_min),
    salary_max: form.salary_max === "" ? null : Number(form.salary_max),
    experience: form.experience, education: form.education, shift: form.shift,
    working_days: form.working_days, employment_type: form.employment_type, work_mode: form.work_mode,
    about: form.about,
    responsibilities: form.responsibilities.split("\n").map((s) => s.trim()).filter(Boolean),
    requirements: form.requirements.split("\n").map((s) => s.trim()).filter(Boolean),
    benefits: form.benefits.split("\n").map((s) => s.trim()).filter(Boolean),
    status: form.status,
    ...overrides,
  });

  const submit = async () => {
    if (!form.title || !form.hiring_brand || !form.location) {
      return toast.error("Title, hiring brand and location are required");
    }
    setBusy(true);
    try {
      if (editing) {
        await api.put(`/jobs/${editing.id}`, buildPayload());
        toast.success("Job updated");
      } else {
        await api.post("/jobs", buildPayload());
        toast.success("Job created");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const archive = async (job) => {
    try {
      const payload = {
        title: job.title, hiring_brand: job.hiring_brand, location: job.location,
        salary_min: job.salary_min, salary_max: job.salary_max,
        experience: job.experience, education: job.education, shift: job.shift,
        working_days: job.working_days, employment_type: job.employment_type, work_mode: job.work_mode,
        about: job.about, responsibilities: job.responsibilities || [],
        requirements: job.requirements || [], benefits: job.benefits || [],
        status: "Archived",
      };
      await api.put(`/jobs/${job.id}`, payload);
      toast.success(`"${job.title}" archived`);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  if (loading) {
    return <div className="space-y-4"><Skeleton className="h-10 w-64 rounded-lg" /><Skeleton className="h-72 rounded-xl" /></div>;
  }

  const MdSelect = ({ label, field, type, required }) => (
    <div className="space-y-1.5">
      <Label>{label}{required && " *"}</Label>
      <Select value={form[field]} onValueChange={(v) => setForm((f) => ({ ...f, [field]: v }))}>
        <SelectTrigger data-testid={`job-form-${field}`}><SelectValue placeholder="Select..." /></SelectTrigger>
        <SelectContent>
          {(md[type] || []).map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight">Jobs</h1>
          <p className="text-muted-foreground mt-1 text-sm">Jobs are archived, never deleted. Client names stay hidden — only hiring brands are shown.</p>
        </div>
        <Button onClick={openCreate} className="rounded-lg" data-testid="admin-jobs-create-button">
          <Plus className="h-4 w-4 mr-2" /> New job
        </Button>
      </div>

      {jobs.length === 0 ? (
        <Card className="rounded-xl border-border shadow-none">
          <EmptyState icon={Briefcase} title="No jobs yet" description="Create your first job posting." actionLabel="New job" onAction={openCreate} testId="admin-jobs-empty" />
        </Card>
      ) : (
        <Card className="rounded-xl border-border shadow-none overflow-hidden">
          <div className="overflow-x-auto">
            <Table data-testid="admin-jobs-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Job</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Salary</TableHead>
                  <TableHead>Applications</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {jobs.map((job) => (
                  <TableRow key={job.id} className="hover:bg-muted/60" data-testid="admin-job-row">
                    <TableCell>
                      <p className="font-medium">{job.title}</p>
                      <p className="text-xs text-muted-foreground">{job.hiring_brand}</p>
                    </TableCell>
                    <TableCell className="text-sm">{job.location}</TableCell>
                    <TableCell className="text-sm text-emerald-700 font-medium">{formatSalary(job.salary_min, job.salary_max)}</TableCell>
                    <TableCell className="font-mono text-sm">{job.applications_count}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`rounded-full text-xs ${JOB_STATUS_STYLES[job.status] || ""}`}>{job.status}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(job)} data-testid={`admin-job-edit-${job.id}`} aria-label="Edit job">
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        {job.status !== "Archived" && (
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground" onClick={() => archive(job)} data-testid={`admin-job-archive-${job.id}`} aria-label="Archive job">
                            <Archive className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl rounded-xl max-h-[90vh] overflow-y-auto" data-testid="admin-job-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit job" : "Create job"}</DialogTitle>
            <DialogDescription>All dropdowns are driven by Master Data. Never expose real client names.</DialogDescription>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Job title *</Label>
              <Input data-testid="job-form-title" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
            </div>
            <MdSelect label="Hiring Brand" field="hiring_brand" type="hiring_brand" required />
            <MdSelect label="Location" field="location" type="location" required />
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Salary min (₹/yr)</Label>
                <Input type="number" min="0" data-testid="job-form-salary-min" value={form.salary_min} onChange={(e) => setForm((f) => ({ ...f, salary_min: e.target.value }))} />
              </div>
              <div className="space-y-1.5">
                <Label>Salary max (₹/yr)</Label>
                <Input type="number" min="0" data-testid="job-form-salary-max" value={form.salary_max} onChange={(e) => setForm((f) => ({ ...f, salary_max: e.target.value }))} />
              </div>
            </div>
            <MdSelect label="Experience" field="experience" type="experience" />
            <MdSelect label="Education" field="education" type="education" />
            <MdSelect label="Shift" field="shift" type="shift" />
            <MdSelect label="Working Days" field="working_days" type="working_days" />
            <MdSelect label="Employment Type" field="employment_type" type="employment_type" />
            <MdSelect label="Work Mode" field="work_mode" type="work_mode" />
            <div className="space-y-1.5">
              <Label>Job Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                <SelectTrigger data-testid="job-form-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {JOB_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>About</Label>
              <Textarea rows={3} data-testid="job-form-about" value={form.about} onChange={(e) => setForm((f) => ({ ...f, about: e.target.value }))} />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Responsibilities (one per line)</Label>
              <Textarea rows={3} data-testid="job-form-responsibilities" value={form.responsibilities} onChange={(e) => setForm((f) => ({ ...f, responsibilities: e.target.value }))} />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Requirements (one per line)</Label>
              <Textarea rows={3} data-testid="job-form-requirements" value={form.requirements} onChange={(e) => setForm((f) => ({ ...f, requirements: e.target.value }))} />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Benefits (one per line)</Label>
              <Textarea rows={3} data-testid="job-form-benefits" value={form.benefits} onChange={(e) => setForm((f) => ({ ...f, benefits: e.target.value }))} />
            </div>
          </div>
          <Button onClick={submit} disabled={busy} className="rounded-lg w-full" data-testid="job-form-submit">
            {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} {editing ? "Save changes" : "Create job"}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
